import { useContext } from "react";

import { useTranslation } from "react-i18next";

import moment from "moment";

import { colors } from "@dotnet/telematics-design-system";

import DateTime from "components/DateTime";
import LanguageContext from "context/language/languageContext";
import TimeZoneContext from "context/timeZone/timeZoneContext";
import { isEnglish } from "shared/functions";
import { body3Regular } from "shared/styles/typography";
import { LOCALES } from "shared/types/enums";

import { TimestampProps } from "./types";

const Timestamp = ({ date }: TimestampProps) => {
    const { textPlaceholder } = colors;

    const { currentTimeZone } = useContext(TimeZoneContext);
    const { language } = useContext(LanguageContext);

    const { t } = useTranslation();

    const renderTimestamp = () => {
        const now = moment().tz(currentTimeZone);
        const startDate = moment.utc(date).tz(currentTimeZone);
        const minutesDiff = now.diff(startDate, "minutes");

        // Less than 1 minute
        if (minutesDiff < 1) {
            return t("Notifications##timestamps##moments ago");
        }

        // Range: 1 minute to 59 minutes
        if (minutesDiff < 60) {
            return t("Notifications##timestamps##minutes ago", {
                minutes: minutesDiff
            });
        }

        // Range: 1 hour to 23 hours
        if (minutesDiff < 1440) {
            const hoursDiff = Math.floor(minutesDiff / 60);
            return t("Notifications##timestamps##hours ago", {
                hours: hoursDiff
            });
        }

        // Range: 1 day to 6 days
        if (minutesDiff < 10080) {
            const daysDiff = Math.floor(minutesDiff / 1440);
            return t("Notifications##timestamps##days ago", { days: daysDiff });
        }

        // Range: 7 days and older
        const isEnglishLng = isEnglish(language);

        return (
            <DateTime
                date={date}
                format="LL"
                locale={isEnglishLng ? LOCALES.English : undefined}
            />
        );
    };

    return (
        <div css={[body3Regular, { color: textPlaceholder }]}>
            {renderTimestamp()}
        </div>
    );
};

export default Timestamp;
